import { DragEvent, JSX, ReactNode, useState, useMemo } from "react";
import { List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import { Typography } from "@platform/design-system";

export type SortableListProps<T> = {
  items: T[];
  emptyMessage?: string;
  keyExtractor?: (item: T) => string;
  renderContent: (item: T, isDragging: boolean) => ReactNode;
  onOrderChange?: (newOrder: T[]) => void;
};

export function SortableList<T>({
  items,
  emptyMessage,
  keyExtractor = (item: T) => String(item),
  renderContent,
  onOrderChange,
}: SortableListProps<T>): JSX.Element {
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [currentOrderKeys, setCurrentOrderKeys] = useState(() =>
    items.length > 0 ? items.map(keyExtractor) : [],
  );

  const itemsMap = useMemo(() => {
    const map = new Map<string, T>();
    items.forEach((item) => map.set(keyExtractor(item), item));
    return map;
  }, [items, keyExtractor]);

  const handleDragOver = (overKey: string) => (e: DragEvent) => {
    e.preventDefault();
    if (!draggingKey || draggingKey === overKey) return;

    const newOrder = [...currentOrderKeys];
    const fromIndex = newOrder.indexOf(draggingKey);
    const toIndex = newOrder.indexOf(overKey);
    if (fromIndex === toIndex) return;

    newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, draggingKey);
    setCurrentOrderKeys(newOrder);
  };

  const handleDragEnd = () => {
    if (onOrderChange) {
      const reorderedItems = currentOrderKeys.map((key) => itemsMap.get(key)!);
      onOrderChange(reorderedItems);
    }
    setDraggingKey(null);
  };

  if (items.length === 0) {
    return (
      <List
        data-testid="ds-sortable-list-empty"
        component="ul"
        sx={{ width: "100%" }}
      >
        <ListItem>
          <ListItemText>
            <Typography variant="body-large">
              {emptyMessage ?? "No items available"}
            </Typography>
          </ListItemText>
        </ListItem>
      </List>
    );
  }

  return (
    <List data-testid="ds-sortable-list" component="ul" sx={{ width: "100%" }}>
      {currentOrderKeys.map((key) => {
        const item = itemsMap.get(key)!;
        const isDragging = key === draggingKey;

        return (
          <ListItem
            data-testid={`ds-sortable-list-item-${key}`}
            key={key}
            draggable
            component="li"
            onDragStart={() => setDraggingKey(key)}
            onDragOver={handleDragOver(key)}
            onDragEnd={handleDragEnd}
            sx={{
              transition: "transform 200ms ease",
              backgroundColor: isDragging ? "rgba(0,0,0,0.05)" : "white",
              opacity: isDragging ? 0.6 : 1,
              mb: 1,
              cursor: "move",
            }}
          >
            <ListItemIcon
              data-testid="ds-sortable-list-item-handle"
              sx={{ minWidth: 36 }}
            >
              <DragIndicator />
            </ListItemIcon>
            {renderContent(item, isDragging)}
          </ListItem>
        );
      })}
    </List>
  );
}
